import React, { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import * as Icons from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell } from "recharts";
import { Loader2, Plus, Trash2, Pencil } from "lucide-react";
import { DashboardLayout } from "../../components/DashboardLayout";
import { StatCard, StatusBadge, money } from "../../components/DashWidgets";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import {
  adminStats, adminLeads, adminUpdateLead, adminApplications, adminUpdateApplication,
  adminUsers, adminCreateUser, adminCommissions, adminUpdateCommission,
  adminGetSettings, adminSetSettings, adminContentList, adminContentCreate, adminContentUpdate, adminContentDelete,
  adminPayouts, adminUpdatePayout, adminChats,
  adminStaff, adminUpdateChat, adminAddChatNote,
  adminRanks, adminCreateRank, adminUpdateRank, adminDeleteRank, adminRecomputeRanks, adminIntegrations,
  adminOperations, adminDocuments, adminReviewDocument, fileUrl, getToken,
} from "../../lib/api";
import { useTabParam, useFocusParam } from "../../lib/useTabParam";
import { useAuth } from "../../lib/auth";

const LEAD_STATUSES = ["New", "In Process", "Approved", "Rejected"];
const APP_STATUSES = ["New", "In Process", "Approved", "Disbursed", "Rejected"];

export default function AdminDashboard() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [tab, setTab] = useTabParam("overview");
  return (
    <DashboardLayout tab={tab} setTab={setTab}>
      {tab === "overview" && <Overview />}
      {tab === "operations" && isAdmin && <Operations setTab={setTab} />}
      {tab === "leads" && <Leads />}
      {tab === "chats" && <ChatsAdmin />}
      {tab === "applications" && <Applications />}
      {tab === "users" && isAdmin && <UsersTab />}
      {tab === "commissions" && isAdmin && <Commissions />}
      {tab === "payouts" && isAdmin && <PayoutsAdmin />}
      {tab === "content" && <ContentTab />}
      {tab === "ranks" && isAdmin && <RanksAdmin />}
      {tab === "documents" && isAdmin && <DocumentsAdmin />}
      {tab === "integrations" && isAdmin && <IntegrationsPanel />}
      {tab === "settings" && isAdmin && <SettingsTab />}
    </DashboardLayout>
  );
}

const OPS_ACCENTS = {
  gold: "text-gold bg-gold/12 border-gold/25",
  amber: "text-amber-400 bg-amber-500/12 border-amber-500/25",
  red: "text-red-400 bg-red-500/12 border-red-500/25",
};
const OPS_CARDS = [
  { key: "new_leads", label: "New Leads", icon: "UserPlus", tab: "leads", accent: "gold" },
  { key: "unassigned_leads", label: "Unassigned Leads", icon: "UserX", tab: "leads", accent: "gold" },
  { key: "overdue_leads", label: "Overdue Leads", icon: "AlarmClock", tab: "chats", accent: "amber" },
  { key: "escalated_leads", label: "Escalated Leads", icon: "AlertTriangle", tab: "chats", accent: "red" },
  { key: "unresolved_chats", label: "Unresolved Chat Leads", icon: "MessagesSquare", tab: "chats", accent: "gold" },
  { key: "pending_rank_rewards", label: "Pending Rank Rewards", icon: "Trophy", tab: "commissions", accent: "amber" },
  { key: "pending_withdrawals", label: "Pending Withdrawals", icon: "Banknote", tab: "payouts", accent: "amber" },
  { key: "documents_awaiting_review", label: "Documents Awaiting Review", icon: "FileText", tab: "documents", accent: "gold" },
];

function Operations({ setTab }) {
  const [ops, setOps] = useState(null);
  useEffect(() => { adminOperations().then(setOps).catch(() => {}); }, []);
  return (
    <div className="space-y-6" data-testid="admin-operations">
      <div>
        <h2 className="font-heading text-2xl font-bold text-white">Operations</h2>
        <p className="text-slate-400 text-sm mt-1">Everything that needs your attention — tap a card to jump to it.</p>
      </div>
      {!ops ? (
        <Loader2 className="w-6 h-6 text-gold animate-spin" />
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {OPS_CARDS.map((c) => {
            const C = Icons[c.icon] || Icons.Circle;
            const val = ops[c.key] ?? 0;
            return (
              <button key={c.key} onClick={() => setTab(c.tab)} data-testid={`ops-card-${c.key}`}
                className="card-dark rounded-2xl p-5 text-left border border-transparent hover:border-gold/40 transition-colors">
                <div className={`w-10 h-10 rounded-xl border flex items-center justify-center mb-3 ${OPS_ACCENTS[c.accent]}`}>
                  <C className="w-5 h-5" />
                </div>
                <div className="font-mono-fin text-2xl font-extrabold text-white">{val}</div>
                <div className="text-sm text-slate-400 mt-0.5">{c.label}</div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Overview() {
  const [s, setS] = useState(null);
  useEffect(() => { adminStats().then(setS).catch(() => {}); }, []);
  if (!s) return <Loader2 className="w-6 h-6 text-gold animate-spin" />;
  const chart = Object.entries(s.lead_breakdown).map(([name, value]) => ({ name, value }));
  const colors = { New: "#3B82F6", "In Process": "#F59E0B", Approved: "#10B981", Rejected: "#EF4444" };
  return (
    <div className="space-y-6" data-testid="admin-overview">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon="UserPlus" label="Total Leads" value={s.total_leads} testid="admin-stat-leads" />
        <StatCard icon="FileText" label="Applications" value={s.total_applications} testid="admin-stat-apps" />
        <StatCard icon="CheckCircle2" label="Approved" value={s.approved_applications} accent="green" testid="admin-stat-approved" />
        <StatCard icon="Coins" label="Commission Payable" value={money(s.pending_commission)} testid="admin-stat-commission" />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon="Users" label="Users" value={s.users} />
        <StatCard icon="Briefcase" label="Agents" value={s.agents} />
        <StatCard icon="Share2" label="Affiliates" value={s.affiliates} />
        <StatCard icon="Banknote" label="Disbursed" value={money(s.disbursed_amount)} accent="green" />
      </div>
      <div className="card-dark rounded-2xl p-6">
        <h3 className="font-heading font-bold text-white mb-4">Lead Pipeline</h3>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chart}>
              <XAxis dataKey="name" stroke="#64748B" fontSize={12} />
              <YAxis stroke="#64748B" fontSize={12} allowDecimals={false} />
              <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                {chart.map((e) => <Cell key={e.name} fill={colors[e.name] || "#D4AF37"} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

function Leads() {
  const [leads, setLeads] = useState([]);
  const { focusId } = useFocusParam();
  const focusRef = useRef(null);
  const load = () => adminLeads().then(setLeads).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (focusId && leads.length) {
      if (focusRef.current) focusRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      else if (!leads.some((l) => l.id === focusId)) toast.info("That lead is no longer available.");
    }
  }, [focusId, leads]);
  const update = async (id, status) => {
    try { await adminUpdateLead(id, { status }); toast.success("Lead updated"); load(); }
    catch { toast.error("Update failed"); }
  };
  return (
    <div data-testid="admin-leads">
      <h2 className="font-heading text-2xl font-bold text-white mb-5">Leads ({leads.length})</h2>
      <div className="space-y-3">
        {leads.map((l) => {
          const focused = l.id === focusId;
          return (
          <div key={l.id} ref={focused ? focusRef : null} className={`card-dark rounded-xl p-4 flex items-center justify-between flex-wrap gap-3 ${focused ? "ring-2 ring-gold" : ""}`} data-testid={`lead-row-${l.id}`}>
            <div className="min-w-0">
              <div className="font-semibold text-white">{l.name} <span className="text-slate-500 text-sm font-normal">· {l.phone}</span></div>
              <div className="text-slate-400 text-sm">{l.loan_type || "—"} {l.amount && `· ₹${l.amount}`} · <span className="text-slate-500">{l.source}</span></div>
            </div>
            <Select value={l.status} onValueChange={(v) => update(l.id, v)}>
              <SelectTrigger data-testid={`lead-status-${l.id}`} className="w-40 bg-obsidian-900/60 border-white/10 h-9 text-white"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-obsidian-700 border-white/10 text-white">
                {LEAD_STATUSES.map((s) => <SelectItem key={s} value={s} className="focus:bg-gold/20 focus:text-white">{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          );
        })}
        {leads.length === 0 && <p className="text-slate-400">No leads yet.</p>}
      </div>
    </div>
  );
}

function Applications() {
  const [apps, setApps] = useState([]);
  const load = () => adminApplications().then(setApps).catch(() => {});
  useEffect(() => { load(); }, []);
  const update = async (id, status) => {
    try { await adminUpdateApplication(id, { status, note: `Marked ${status} by admin` }); toast.success(`Application ${status}`); load(); }
    catch { toast.error("Update failed"); }
  };
  return (
    <div data-testid="admin-applications">
      <h2 className="font-heading text-2xl font-bold text-white mb-5">Applications ({apps.length})</h2>
      <div className="space-y-3">
        {apps.map((a) => (
          <div key={a.id} className="card-dark rounded-xl p-4 flex items-center justify-between flex-wrap gap-3" data-testid={`admin-app-row-${a.id}`}>
            <div>
              <div className="font-semibold text-white">{a.customer_name} <span className="text-slate-500 text-sm font-normal">· {a.customer_phone}</span></div>
              <div className="text-slate-400 text-sm">{a.loan_type} · <span className="font-mono-fin text-gold">{money(a.amount)}</span> {a.agent_id && "· via Agent"}</div>
            </div>
            <Select value={a.status} onValueChange={(v) => update(a.id, v)}>
              <SelectTrigger data-testid={`app-status-${a.id}`} className="w-40 bg-obsidian-900/60 border-white/10 h-9 text-white"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-obsidian-700 border-white/10 text-white">
                {APP_STATUSES.map((s) => <SelectItem key={s} value={s} className="focus:bg-gold/20 focus:text-white">{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        ))}
        {apps.length === 0 && <p className="text-slate-400">No applications yet.</p>}
      </div>
    </div>
  );
}

function UsersTab() {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", role: "staff" });
  const [loading, setLoading] = useState(false);
  const load = () => adminUsers().then(setUsers).catch(() => {});
  useEffect(() => { load(); }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const create = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.password) { toast.error("Fill all fields"); return; }
    setLoading(true);
    try { await adminCreateUser(form); toast.success("User created"); setOpen(false); setForm({ name: "", email: "", phone: "", password: "", role: "staff" }); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); } finally { setLoading(false); }
  };
  return (
    <div data-testid="admin-users">
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-heading text-2xl font-bold text-white">Users & Team ({users.length})</h2>
        <Button onClick={() => setOpen(true)} data-testid="add-user-button" className="gold-gradient text-obsidian-900 font-bold rounded-full"><Plus className="w-4 h-4 mr-1" /> Add User</Button>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        {users.map((u) => (
          <div key={u.id} className="card-dark rounded-xl p-4 flex items-center justify-between" data-testid={`user-row-${u.id}`}>
            <div>
              <div className="font-semibold text-white">{u.name}</div>
              <div className="text-slate-500 text-xs">{u.email}</div>
              {u.referral_code && <div className="text-slate-600 text-xs font-mono-fin mt-0.5">{u.referral_code}</div>}
            </div>
            <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-gold/15 text-gold capitalize">{u.role}</span>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="card-dark border-gold/30 max-w-md" data-testid="add-user-modal">
          <DialogHeader><DialogTitle className="font-heading text-xl font-extrabold text-white">Add User</DialogTitle></DialogHeader>
          <form onSubmit={create} className="space-y-3.5">
            <Input data-testid="user-name" placeholder="Name" value={form.name} onChange={(e) => set("name", e.target.value)} className="bg-obsidian-900/60 border-white/10 h-11 text-white" />
            <Input data-testid="user-email" type="email" placeholder="Email" value={form.email} onChange={(e) => set("email", e.target.value)} className="bg-obsidian-900/60 border-white/10 h-11 text-white" />
            <Input data-testid="user-phone" placeholder="Phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="bg-obsidian-900/60 border-white/10 h-11 text-white" />
            <Input data-testid="user-password" type="password" placeholder="Password" value={form.password} onChange={(e) => set("password", e.target.value)} className="bg-obsidian-900/60 border-white/10 h-11 text-white" />
            <Select value={form.role} onValueChange={(v) => set("role", v)}>
              <SelectTrigger data-testid="user-role" className="bg-obsidian-900/60 border-white/10 h-11 text-white"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-obsidian-700 border-white/10 text-white">
                {["admin", "staff", "agent", "affiliate", "customer"].map((r) => <SelectItem key={r} value={r} className="focus:bg-gold/20 focus:text-white capitalize">{r}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button type="submit" disabled={loading} data-testid="user-submit" className="w-full gold-gradient text-obsidian-900 font-bold h-11 rounded-full">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Create User"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Commissions() {
  const [comms, setComms] = useState([]);
  const load = () => adminCommissions().then(setComms).catch(() => {});
  useEffect(() => { load(); }, []);
  const update = async (id, status) => {
    try { await adminUpdateCommission(id, { status }); toast.success(`Marked ${status}`); load(); } catch { toast.error("Failed"); }
  };
  const total = comms.reduce((a, c) => a + c.amount, 0);
  const paid = comms.filter((c) => c.status === "paid").reduce((a, c) => a + c.amount, 0);
  return (
    <div data-testid="admin-commissions">
      <h2 className="font-heading text-2xl font-bold text-white mb-5">Commissions</h2>
      <div className="grid grid-cols-3 gap-4 mb-6">
        <StatCard icon="Coins" label="Total" value={money(total)} />
        <StatCard icon="CheckCircle2" label="Paid" value={money(paid)} accent="green" />
        <StatCard icon="Clock" label="Pending" value={money(total - paid)} />
      </div>
      <div className="space-y-3">
        {comms.map((c) => (
          <div key={c.id} className="card-dark rounded-xl p-4 flex items-center justify-between flex-wrap gap-3" data-testid={`comm-row-${c.id}`}>
            <div>
              <div className="font-semibold text-white">{c.user_name} <span className="font-mono-fin text-gold">· {money(c.amount)}</span></div>
              <div className="text-slate-500 text-xs capitalize">{c.type} · {c.rate}%</div>
            </div>
            <div className="flex items-center gap-2">
              <Select value={c.status} onValueChange={(v) => update(c.id, v)}>
                <SelectTrigger data-testid={`comm-status-${c.id}`} className="w-32 bg-obsidian-900/60 border-white/10 h-9 text-white"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-obsidian-700 border-white/10 text-white">
                  {["pending", "approved", "paid"].map((s) => <SelectItem key={s} value={s} className="focus:bg-gold/20 focus:text-white capitalize">{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        ))}
        {comms.length === 0 && <p className="text-slate-400">No commissions yet.</p>}
      </div>
    </div>
  );
}

const EMPTY_CONTENT = { type: "news", title: "", body: "", image_url: "", link: "", published: true, order: 0 };

function ContentTab() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_CONTENT);
  const [loading, setLoading] = useState(false);
  const load = () => adminContentList().then(setItems).catch(() => {});
  useEffect(() => { load(); }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openNew = () => { setEditing(null); setForm(EMPTY_CONTENT); setOpen(true); };
  const openEdit = (it) => { setEditing(it.id); setForm({ ...EMPTY_CONTENT, ...it }); setOpen(true); };
  const save = async (e) => {
    e.preventDefault();
    if (!form.title) { toast.error("Title required"); return; }
    setLoading(true);
    const payload = { type: form.type, title: form.title, body: form.body, image_url: form.image_url, link: form.link, published: form.published, order: Number(form.order) || 0 };
    try {
      if (editing) await adminContentUpdate(editing, payload); else await adminContentCreate(payload);
      toast.success("Saved"); setOpen(false); load();
    } catch { toast.error("Save failed"); } finally { setLoading(false); }
  };
  const remove = async (id) => { try { await adminContentDelete(id); toast.success("Deleted"); load(); } catch { toast.error("Failed"); } };
  return (
    <div data-testid="admin-content">
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-heading text-2xl font-bold text-white">Website Content</h2>
        <Button onClick={openNew} data-testid="add-content-button" className="gold-gradient text-obsidian-900 font-bold rounded-full"><Plus className="w-4 h-4 mr-1" /> New Post</Button>
      </div>
      <p className="text-slate-400 text-sm mb-5">Manage the News / Latest / Upcoming posts shown on your homepage.</p>
      <div className="grid sm:grid-cols-2 gap-4">
        {items.map((it) => (
          <div key={it.id} className="card-dark rounded-xl p-4" data-testid={`content-row-${it.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-gold/15 text-gold capitalize">{it.type}</span>
                <div className="font-semibold text-white mt-2">{it.title}</div>
                <div className="text-slate-500 text-xs mt-1 line-clamp-2">{it.body}</div>
                <div className={`text-xs mt-2 ${it.published ? "text-approval" : "text-slate-500"}`}>{it.published ? "Published" : "Hidden"}</div>
              </div>
              <div className="flex flex-col gap-1.5 shrink-0">
                <button onClick={() => openEdit(it)} data-testid={`edit-content-${it.id}`} className="p-2 rounded-lg bg-white/5 text-slate-300 hover:text-gold"><Pencil className="w-4 h-4" /></button>
                <button onClick={() => remove(it.id)} data-testid={`delete-content-${it.id}`} className="p-2 rounded-lg bg-red-500/10 text-red-400"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="card-dark border-gold/30 max-w-lg max-h-[92vh] overflow-y-auto" data-testid="content-modal">
          <DialogHeader><DialogTitle className="font-heading text-xl font-extrabold text-white">{editing ? "Edit" : "New"} Post</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-3.5">
            <Select value={form.type} onValueChange={(v) => set("type", v)}>
              <SelectTrigger data-testid="content-type" className="bg-obsidian-900/60 border-white/10 h-11 text-white"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-obsidian-700 border-white/10 text-white">
                {["news", "latest", "upcoming", "announcement"].map((t) => <SelectItem key={t} value={t} className="focus:bg-gold/20 focus:text-white capitalize">{t}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input data-testid="content-title" placeholder="Title" value={form.title} onChange={(e) => set("title", e.target.value)} className="bg-obsidian-900/60 border-white/10 h-11 text-white" />
            <Textarea data-testid="content-body" placeholder="Body text" value={form.body} onChange={(e) => set("body", e.target.value)} className="bg-obsidian-900/60 border-white/10 text-white" />
            <Input data-testid="content-image" placeholder="Image URL (optional)" value={form.image_url} onChange={(e) => set("image_url", e.target.value)} className="bg-obsidian-900/60 border-white/10 h-11 text-white" />
            <Input data-testid="content-link" placeholder="Link (optional)" value={form.link} onChange={(e) => set("link", e.target.value)} className="bg-obsidian-900/60 border-white/10 h-11 text-white" />
            <div className="flex items-center justify-between">
              <Label className="text-slate-300 text-sm">Published</Label>
              <Switch checked={form.published} onCheckedChange={(v) => set("published", v)} data-testid="content-published" />
            </div>
            <Button type="submit" disabled={loading} data-testid="content-save" className="w-full gold-gradient text-obsidian-900 font-bold h-11 rounded-full">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Save Post"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const PAYOUT_STATUSES = ["pending", "approved", "paid", "rejected"];

function PayoutsAdmin() {
  const [rows, setRows] = useState([]);
  const { focusId } = useFocusParam();
  const focusRef = useRef(null);
  const load = () => adminPayouts().then(setRows).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (focusId && rows.length && focusRef.current) focusRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [focusId, rows]);
  const update = async (id, status) => {
    try { await adminUpdatePayout(id, { status }); toast.success(`Marked ${status}`); load(); } catch { toast.error("Failed"); }
  };
  const pending = rows.filter((r) => r.status === "pending").reduce((a, r) => a + r.amount, 0);
  const paid = rows.filter((r) => r.status === "paid").reduce((a, r) => a + r.amount, 0);
  return (
    <div data-testid="admin-payouts">
      <h2 className="font-heading text-2xl font-bold text-white mb-5">Payout Requests ({rows.length})</h2>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        <StatCard icon="Clock" label="Pending Requests" value={money(pending)} />
        <StatCard icon="CheckCircle2" label="Paid Out" value={money(paid)} accent="green" />
        <StatCard icon="Banknote" label="Total Requests" value={rows.length} />
      </div>
      <div className="space-y-3">
        {rows.map((r) => {
          const focused = r.id === focusId;
          return (
          <div key={r.id} ref={focused ? focusRef : null} className={`card-dark rounded-xl p-4 flex items-center justify-between flex-wrap gap-3 ${focused ? "ring-2 ring-gold" : ""}`} data-testid={`admin-payout-row-${r.id}`}>
            <div className="min-w-0">
              <div className="font-semibold text-white">{r.user_name} <span className="font-mono-fin text-gold">· {money(r.amount)}</span> <span className="text-xs text-slate-500 capitalize">({r.role})</span></div>
              <div className="text-slate-400 text-xs">
                {r.method === "upi" ? `UPI: ${r.upi_id}` : `A/C: ${r.account_number} · IFSC: ${r.ifsc} · ${r.account_name}`}
              </div>
              {r.note && <div className="text-slate-600 text-xs mt-0.5">Note: {r.note}</div>}
            </div>
            <div className="flex items-center gap-2">
              <StatusBadge status={r.status} />
              <Select value={r.status} onValueChange={(v) => update(r.id, v)}>
                <SelectTrigger data-testid={`payout-status-${r.id}`} className="w-32 bg-obsidian-900/60 border-white/10 h-9 text-white capitalize"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-obsidian-700 border-white/10 text-white">
                  {PAYOUT_STATUSES.map((s) => <SelectItem key={s} value={s} className="focus:bg-gold/20 focus:text-white capitalize">{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          );
        })}
        {rows.length === 0 && <p className="text-slate-400">No payout requests yet.</p>}
      </div>
    </div>
  );
}

function DocumentsAdmin() {
  const [docs, setDocs] = useState([]);
  const { focusId } = useFocusParam();
  const focusRef = useRef(null);
  const load = () => adminDocuments().then(setDocs).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (focusId && docs.length && focusRef.current) focusRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [focusId, docs]);
  const review = async (id) => { try { await adminReviewDocument(id); toast.success("Marked reviewed"); load(); } catch { toast.error("Failed"); } };
  const openDoc = (path) => window.open(`${fileUrl(path)}?auth=${encodeURIComponent(getToken() || "")}`, "_blank");
  const fmt = (t) => { try { return new Date(t).toLocaleString(); } catch { return t; } };
  const pending = docs.filter((d) => !d.reviewed).length;
  return (
    <div data-testid="admin-documents">
      <h2 className="font-heading text-2xl font-bold text-white mb-1">Documents ({docs.length})</h2>
      <p className="text-slate-400 text-sm mb-5">{pending} awaiting review. Uploaded KYC & business documents across all members.</p>
      <div className="space-y-3">
        {docs.map((d) => {
          const focused = d.id === focusId;
          return (
          <div key={d.id} ref={focused ? focusRef : null} className={`card-dark rounded-xl p-4 flex items-center justify-between flex-wrap gap-3 ${focused ? "ring-2 ring-gold" : ""}`} data-testid={`admin-doc-row-${d.id}`}>
            <div className="min-w-0">
              <div className="font-semibold text-white truncate">{d.original_filename} <span className="text-slate-500 text-xs font-normal">· {d.doc_type}</span></div>
              <div className="text-slate-400 text-xs">{d.owner_name} {d.owner_email && `· ${d.owner_email}`} · {fmt(d.created_at)}</div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {d.reviewed ? (
                <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-approval/15 text-approval">Reviewed</span>
              ) : (
                <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-amber-500/15 text-amber-400">Awaiting</span>
              )}
              <button onClick={() => openDoc(d.storage_path)} data-testid={`doc-view-${d.id}`} className="text-xs text-gold border border-gold/40 rounded-full px-3 py-1.5">View</button>
              {!d.reviewed && <button onClick={() => review(d.id)} data-testid={`doc-review-${d.id}`} className="text-xs gold-gradient text-obsidian-900 font-semibold rounded-full px-3 py-1.5">Mark reviewed</button>}
            </div>
          </div>
          );
        })}
        {docs.length === 0 && <p className="text-slate-400">No documents uploaded yet.</p>}
      </div>
    </div>
  );
}

function ChatsAdmin() {
  const [sessions, setSessions] = useState([]);
  const [sel, setSel] = useState(null);
  const [staff, setStaff] = useState([]);
  const [note, setNote] = useState("");
  const [fStatus, setFStatus] = useState("all");
  const { focusId } = useFocusParam();
  const load = () => adminChats().then((d) => { setSessions(d); setSel((cur) => cur ? (d.find((x) => x.session_id === cur.session_id) || null) : null); }).catch(() => {});
  useEffect(() => { load(); adminStaff().then(setStaff).catch(() => {}); }, []);
  useEffect(() => {
    if (focusId && sessions.length) {
      const found = sessions.find((x) => x.session_id === focusId);
      if (found) setSel(found);
      else toast.info("That conversation is no longer available.");
    }
  }, [focusId, sessions]);
  const fmt = (t) => { try { return new Date(t).toLocaleString(); } catch { return t; } };
  const lastUser = (s) => [...(s.messages || [])].reverse().find((m) => m.role === "user");
  const STATUSES = ["New", "Assigned", "Contacted", "Follow-up", "Converted", "Closed", "Spam"];
  const filtered = sessions.filter((s) => fStatus === "all" || (s.meta?.status || "New") === fStatus);
  const meta = sel?.meta || {};
  const patch = async (p) => { await adminUpdateChat(sel.session_id, p); toast.success("Updated"); load(); };
  const addNote = async () => { if (!note.trim()) return; await adminAddChatNote(sel.session_id, note.trim()); setNote(""); toast.success("Note added"); load(); };
  return (
    <div data-testid="admin-chats">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <h2 className="font-heading text-2xl font-bold text-white">Chat Leads ({filtered.length})</h2>
        <Select value={fStatus} onValueChange={setFStatus}>
          <SelectTrigger data-testid="chat-filter-status" className="w-40 bg-obsidian-900/60 border-white/10 h-9 text-white"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-obsidian-700 border-white/10 text-white">
            <SelectItem value="all" className="focus:bg-gold/20 focus:text-white">All statuses</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s} className="focus:bg-gold/20 focus:text-white">{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {sessions.length === 0 ? (
        <p className="text-slate-400">No chatbot conversations yet.</p>
      ) : (
        <div className="grid lg:grid-cols-5 gap-4">
          <div className="lg:col-span-2 space-y-2 max-h-[70vh] overflow-y-auto pr-1">
            {filtered.map((s) => {
              const lu = lastUser(s);
              const active = sel?.session_id === s.session_id;
              const st = s.meta?.status || "New";
              return (
                <button key={s.session_id} onClick={() => setSel(s)} data-testid={`chat-session-${s.session_id}`}
                  className={`w-full text-left card-dark rounded-xl p-3 border transition-colors ${active ? "border-gold" : "border-transparent hover:border-white/15"}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-white text-sm font-semibold truncate">
                      {s.lead ? s.lead.name : "Anonymous visitor"}
                      {s.lead?.phone && <span className="text-slate-500 font-normal"> · {s.lead.phone}</span>}
                    </div>
                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-white/10 text-slate-300 shrink-0">{st}</span>
                  </div>
                  <div className="text-slate-500 text-xs mt-0.5 truncate">{lu ? lu.content : `${s.count} messages`}</div>
                  <div className="text-slate-600 text-[10px] mt-1 flex items-center justify-between">
                    <span>{fmt(s.last)} · {s.count} msgs</span>
                    {s.meta?.assigned_to_name && <span className="text-gold">→ {s.meta.assigned_to_name}</span>}
                  </div>
                </button>
              );
            })}
          </div>
          <div className="lg:col-span-3 card-dark rounded-2xl p-4 min-h-[300px]" data-testid="chat-transcript">
            {!sel ? (
              <p className="text-slate-400 text-sm">Select a conversation to read the full transcript.</p>
            ) : (
              <>
                <div className="flex items-center justify-between flex-wrap gap-2 pb-3 border-b border-white/10 mb-3">
                  <div className="text-white font-semibold">{sel.lead ? sel.lead.name : "Anonymous visitor"}
                    {sel.lead?.phone && <span className="text-slate-500 font-normal"> · {sel.lead.phone}</span>}</div>
                  {sel.lead?.phone && (
                    <div className="flex gap-2">
                      <a href={`tel:${sel.lead.phone}`} className="text-xs rounded-full border border-gold/40 text-gold px-3 py-1.5">Call</a>
                      <a href={`https://wa.me/91${sel.lead.phone}`} target="_blank" rel="noreferrer" className="text-xs rounded-full gold-gradient text-obsidian-900 font-semibold px-3 py-1.5">WhatsApp</a>
                    </div>
                  )}
                </div>
                <div className="grid sm:grid-cols-3 gap-2 mb-3">
                  <Select value={meta.assigned_to || "unassigned"} onValueChange={(v) => patch({ assigned_to: v === "unassigned" ? "" : v })}>
                    <SelectTrigger data-testid="chat-assign" className="bg-obsidian-900/60 border-white/10 h-9 text-white text-xs"><SelectValue placeholder="Assign to" /></SelectTrigger>
                    <SelectContent className="bg-obsidian-700 border-white/10 text-white">
                      <SelectItem value="unassigned" className="focus:bg-gold/20 focus:text-white">Unassigned</SelectItem>
                      {staff.map((st) => <SelectItem key={st.id} value={st.id} className="focus:bg-gold/20 focus:text-white">{st.name} ({st.role})</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Select value={meta.status || "New"} onValueChange={(v) => patch({ status: v })}>
                    <SelectTrigger data-testid="chat-status" className="bg-obsidian-900/60 border-white/10 h-9 text-white text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-obsidian-700 border-white/10 text-white">
                      {STATUSES.map((s) => <SelectItem key={s} value={s} className="focus:bg-gold/20 focus:text-white">{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Input type="date" data-testid="chat-followup" defaultValue={meta.follow_up || ""} onBlur={(e) => e.target.value && patch({ follow_up: e.target.value })} className="bg-obsidian-900/60 border-white/10 h-9 text-white text-xs" />
                </div>
                <div className="space-y-2 max-h-[42vh] overflow-y-auto">
                  {sel.messages.map((m, i) => (
                    <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-gold/20 text-white" : "bg-obsidian-900/70 text-slate-200 border border-white/10"}`}>{m.content}</div>
                    </div>
                  ))}
                </div>
                <div className="mt-3 pt-3 border-t border-white/10">
                  <div className="text-xs uppercase tracking-widest text-gold font-semibold mb-2">Internal Notes</div>
                  <div className="space-y-1.5 mb-2 max-h-28 overflow-y-auto">
                    {(meta.notes || []).map((n, i) => (
                      <div key={i} className="text-xs text-slate-300 bg-obsidian-900/60 rounded-lg px-2.5 py-1.5"><span className="text-gold">{n.by}:</span> {n.text}</div>
                    ))}
                    {(!meta.notes || meta.notes.length === 0) && <div className="text-slate-600 text-xs">No internal notes yet.</div>}
                  </div>
                  <div className="flex gap-2">
                    <Input value={note} onChange={(e) => setNote(e.target.value)} data-testid="chat-note-input" placeholder="Add an internal note (not visible to customer)" className="bg-obsidian-900/60 border-white/10 h-9 text-white text-xs" />
                    <Button onClick={addNote} data-testid="chat-note-add" className="gold-gradient text-obsidian-900 font-bold rounded-lg h-9 px-4">Add</Button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const RANK_EMPTY = { code: "", name: "", min_personal: 0, min_team: 0, min_directs: 0, reward: 0, color: "#D4AF37", badge: "Award", order: 0, active: true };

function RanksAdmin() {
  const [ranks, setRanks] = useState([]);
  const [form, setForm] = useState(RANK_EMPTY);
  const [editing, setEditing] = useState(null);
  const load = () => adminRanks().then(setRanks).catch(() => {});
  useEffect(() => { load(); }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    if (!form.name || !form.code) { toast.error("Name and code are required"); return; }
    try {
      if (editing) await adminUpdateRank(editing, form); else await adminCreateRank(form);
      toast.success("Saved"); setForm(RANK_EMPTY); setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const edit = (r) => { setEditing(r.id); setForm({ ...RANK_EMPTY, ...r }); };
  const del = async (id) => { await adminDeleteRank(id); toast.success("Deleted"); load(); };
  const recompute = async () => { try { const r = await adminRecomputeRanks(); toast.success(`Recomputed ${r.recomputed} members`); } catch { toast.error("Failed"); } };
  const num = (k, label) => (
    <div><label className="text-slate-400 text-xs">{label}</label>
      <Input type="number" value={form[k]} onChange={(e) => set(k, Number(e.target.value))} className="bg-obsidian-900/60 border-white/10 h-9 text-white" /></div>
  );
  return (
    <div data-testid="admin-ranks">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <h2 className="font-heading text-2xl font-bold text-white">Ranks & Rewards ({ranks.length})</h2>
        <Button onClick={recompute} data-testid="recompute-ranks" className="gold-gradient text-obsidian-900 font-bold rounded-full">Recompute all members</Button>
      </div>
      <div className="space-y-2 mb-6">
        {ranks.map((r) => (
          <div key={r.id} className="card-dark rounded-xl p-3 flex items-center justify-between gap-3 flex-wrap" data-testid={`rank-row-${r.code}`}>
            <div className="flex items-center gap-2 min-w-0">
              <span className="w-3 h-3 rounded-full" style={{ backgroundColor: r.color }} />
              <div className="text-white font-semibold">{r.name} <span className="text-slate-500 text-xs">({r.code})</span></div>
            </div>
            <div className="text-slate-400 text-xs">PV ≥ {money(r.min_personal)} · TV ≥ {money(r.min_team)} · Directs ≥ {r.min_directs} · Reward {money(r.reward)}</div>
            <div className="flex gap-2">
              <button onClick={() => edit(r)} data-testid={`rank-edit-${r.code}`} className="text-xs text-gold border border-gold/40 rounded-full px-3 py-1">Edit</button>
              <button onClick={() => del(r.id)} data-testid={`rank-delete-${r.code}`} className="text-xs text-red-400 border border-red-400/40 rounded-full px-3 py-1">Delete</button>
            </div>
          </div>
        ))}
      </div>
      <div className="card-dark rounded-2xl p-5">
        <div className="text-xs uppercase tracking-widest text-gold font-semibold mb-3">{editing ? "Edit rank" : "Add new rank"}</div>
        <div className="grid sm:grid-cols-3 gap-3">
          <div><label className="text-slate-400 text-xs">Name</label><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="rank-name" className="bg-obsidian-900/60 border-white/10 h-9 text-white" /></div>
          <div><label className="text-slate-400 text-xs">Code</label><Input value={form.code} onChange={(e) => set("code", e.target.value)} data-testid="rank-code" className="bg-obsidian-900/60 border-white/10 h-9 text-white" /></div>
          <div><label className="text-slate-400 text-xs">Color</label><Input value={form.color} onChange={(e) => set("color", e.target.value)} className="bg-obsidian-900/60 border-white/10 h-9 text-white" /></div>
          {num("min_personal", "Min Personal Vol")}
          {num("min_team", "Min Team Vol")}
          {num("min_directs", "Min Directs")}
          {num("reward", "Reward (₹)")}
          {num("order", "Order")}
        </div>
        <div className="flex gap-2 mt-4">
          <Button onClick={save} data-testid="rank-save" className="gold-gradient text-obsidian-900 font-bold rounded-full">{editing ? "Update Rank" : "Create Rank"}</Button>
          {editing && <Button onClick={() => { setEditing(null); setForm(RANK_EMPTY); }} variant="outline" className="rounded-full border-white/20 text-slate-300">Cancel</Button>}
        </div>
      </div>
    </div>
  );
}

function IntegrationsPanel() {
  const [data, setData] = useState(null);
  useEffect(() => { adminIntegrations().then(setData).catch(() => {}); }, []);
  if (!data) return <p className="text-slate-400">Loading…</p>;
  const backend = process.env.REACT_APP_BACKEND_URL || "";
  const Card = ({ title, status, rows, hint, testid }) => {
    const ok = status === "Connected" || status === "Configured";
    return (
      <div className="card-dark rounded-2xl p-5" data-testid={testid}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-white">{title}</h3>
          <span className={`text-xs px-2.5 py-1 rounded-full whitespace-nowrap shrink-0 ${ok ? "bg-approval/15 text-approval" : "bg-amber-500/15 text-amber-400"}`}>{status}</span>
        </div>
        <div className="space-y-1.5 text-sm">
          {rows.map(([k, v]) => <div key={k} className="flex justify-between gap-3"><span className="text-slate-500">{k}</span><span className="text-slate-300 font-mono-fin truncate max-w-[60%] text-right">{v ?? "—"}</span></div>)}
        </div>
        {hint && <p className="text-slate-500 text-xs mt-3 leading-relaxed">{hint}</p>}
      </div>
    );
  };
  return (
    <div data-testid="admin-integrations">
      <h2 className="font-heading text-2xl font-bold text-white mb-1">Integrations</h2>
      <p className="text-slate-400 text-sm mb-5">Secrets are masked and never exposed. Add credentials to the backend environment to activate — status shows "Configuration Required" until then.</p>
      <div className="grid md:grid-cols-3 gap-4">
        <Card testid="integration-whatsapp" title="WhatsApp (Twilio)" status={data.whatsapp.status}
          rows={[["Account SID", data.whatsapp.account_sid], ["Sender", data.whatsapp.sender], ["Signature verify", data.whatsapp.signature_verification ? "On" : "Off"], ["Webhook", backend + data.whatsapp.webhook_url]]}
          hint="In Twilio, set the WhatsApp 'When a message comes in' webhook to the URL above." />
        <Card testid="integration-inbound-email" title="Inbound Email (SendGrid)" status={data.inbound_email.status}
          rows={[["API Key", data.inbound_email.api_key], ["Inbound Domain", data.inbound_email.inbound_domain], ["Secret set", data.inbound_email.secret_set ? "Yes" : "No"], ["Webhook", backend + data.inbound_email.webhook_url]]}
          hint="Point your domain MX to mx.sendgrid.net and set the Inbound Parse URL to the webhook above (append ?secret=…)." />
        <Card testid="integration-outbound-email" title="Outbound Email" status={data.outbound_email.status}
          rows={[["Provider", data.outbound_email.provider], ["From", data.outbound_email.from_name]]}
          hint="Live: lead acknowledgements, payout & rank notifications." />
      </div>
    </div>
  );
}

function SettingsTab() {
  const [form, setForm] = useState({ agent_rate: "", affiliate_rate: "", sub_agent_rate: "", sla_escalation_days: 3 });
  const [loading, setLoading] = useState(false);
  useEffect(() => { adminGetSettings().then((s) => setForm({ agent_rate: s.agent_rate, affiliate_rate: s.affiliate_rate, sub_agent_rate: s.sub_agent_rate || 0.5, sla_escalation_days: s.sla_escalation_days ?? 3 })).catch(() => {}); }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async (e) => {
    e.preventDefault();
    const days = Number(form.sla_escalation_days);
    if (!Number.isFinite(days) || days < 1 || days > 30) { toast.error("SLA escalation days must be between 1 and 30"); return; }
    setLoading(true);
    try {
      await adminSetSettings({ agent_rate: Number(form.agent_rate), affiliate_rate: Number(form.affiliate_rate), sub_agent_rate: Number(form.sub_agent_rate), sla_escalation_days: days });
      toast.success("Settings saved");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); } finally { setLoading(false); }
  };
  return (
    <div className="max-w-lg" data-testid="admin-settings">
      <h2 className="font-heading text-2xl font-bold text-white mb-5">Commission & SLA Settings</h2>
      <form onSubmit={save} className="card-dark rounded-2xl p-6 space-y-4" data-testid="settings-form">
        <div>
          <Label className="text-slate-300 text-sm mb-1.5 block">Agent Commission (%)</Label>
          <Input data-testid="setting-agent-rate" type="number" step="0.1" value={form.agent_rate} onChange={(e) => set("agent_rate", e.target.value)} className="bg-obsidian-900/60 border-white/10 h-11 text-white" />
        </div>
        <div>
          <Label className="text-slate-300 text-sm mb-1.5 block">Affiliate Commission (%)</Label>
          <Input data-testid="setting-affiliate-rate" type="number" step="0.1" value={form.affiliate_rate} onChange={(e) => set("affiliate_rate", e.target.value)} className="bg-obsidian-900/60 border-white/10 h-11 text-white" />
        </div>
        <div>
          <Label className="text-slate-300 text-sm mb-1.5 block">Sub-Agent Commission (%)</Label>
          <Input data-testid="setting-subagent-rate" type="number" step="0.1" value={form.sub_agent_rate} onChange={(e) => set("sub_agent_rate", e.target.value)} className="bg-obsidian-900/60 border-white/10 h-11 text-white" />
        </div>
        <div>
          <Label className="text-slate-300 text-sm mb-1.5 block">SLA Escalation Days</Label>
          <Input data-testid="setting-sla-days" type="number" min="1" max="30" step="1" value={form.sla_escalation_days} onChange={(e) => set("sla_escalation_days", e.target.value)} className="bg-obsidian-900/60 border-white/10 h-11 text-white" />
          <p className="text-slate-500 text-xs mt-1.5">Overdue assigned chat leads are escalated to admin after this many days (1–30). Default 3.</p>
        </div>
        <Button type="submit" disabled={loading} data-testid="settings-save" className="w-full gold-gradient text-obsidian-900 font-bold h-11 rounded-full">
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Save Settings"}
        </Button>
      </form>
    </div>
  );
}
